/**
 * Реєстр бренд-асетів.
 *
 * Це єдине джерело правди про те, які картинки існують і де вони лежать.
 * На нього спираються три речі: вебзастосунок (шляхи), промти для Gemini
 * (що саме генерувати і під яким ім'ям), і CI-перевірка `check:assets`,
 * яка падає, якщо файл із реєстру відсутній у `apps/web/public`.
 *
 * Художній напрям — оригінальний, у стилістиці мобільних аренних ігор
 * (жирний контур, соковиті градієнти, підкреслена перспектива знизу).
 * Персонажі власні, не запозичені: так безпечно з боку прав і, головне,
 * генератори зображень стабільно віддають результат.
 */

import type { SubjectId } from './domain.js';

export const BRAND_ROOT = '/brand';

// ─────────────────────────────────────────────────────────────────────────────
// Палітра — синхронізована з apps/web/src/styles/tokens.css
// ─────────────────────────────────────────────────────────────────────────────

export const BRAND_COLORS = {
  /** Фон арени — глибокий синьо-фіолетовий. */
  bgDeep: '#160B33',
  bgMid: '#241357',
  bgPanel: '#2E1A6B',
  /** Головний акцент — електрична жовта, колір нагороди. */
  gold: '#FFC61A',
  goldDeep: '#FF9500',
  /** Другорядний акцент — бірюза інтерфейсу. */
  cyan: '#22D3EE',
  magenta: '#F038C8',
  green: '#3DDC64',
  red: '#FF3B5C',
  ink: '#0B0620',
  paper: '#FFFFFF',
} as const;

/** Колір-ключ кожного предмета: використовується і в UI, і в промтах. */
export const SUBJECT_THEME: Record<SubjectId, { name: string; color: string; accent: string }> = {
  math: { name: 'Блейз', color: '#FF7A2F', accent: '#FFC61A' },
  ukrainian: { name: 'Квіл', color: '#3DDC64', accent: '#B6FF5C' },
  english: { name: 'Нова', color: '#22D3EE', accent: '#7C5CFF' },
  science: { name: 'Флора', color: '#9B5CFF', accent: '#F038C8' },
  logic: { name: 'Ланка', color: '#FF3B5C', accent: '#FFC61A' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Реєстр файлів
// ─────────────────────────────────────────────────────────────────────────────

export interface BrandAsset {
  /** Шлях від кореня сайту. Саме він іде в src/img. */
  path: string;
  /** Розмір у пікселях, який має віддати генератор. */
  size: [number, number];
  /** Чи потрібен прозорий фон. */
  transparent: boolean;
  /** Короткий опис для промта і для ревʼю. */
  purpose: string;
}

const asset = (
  path: string,
  size: [number, number],
  transparent: boolean,
  purpose: string,
): BrandAsset => ({ path, size, transparent, purpose });

/** Маскоти предметів — обличчя кожного напряму навчання. */
export const HERO_ASSETS: Record<SubjectId, BrandAsset> = {
  math: asset(`${BRAND_ROOT}/heroes/hero-math.png`, [1024, 1024], true, 'Маскот математики'),
  ukrainian: asset(`${BRAND_ROOT}/heroes/hero-ukrainian.png`, [1024, 1024], true, 'Маскот української'),
  english: asset(`${BRAND_ROOT}/heroes/hero-english.png`, [1024, 1024], true, 'Маскот англійської'),
  science: asset(`${BRAND_ROOT}/heroes/hero-science.png`, [1024, 1024], true, 'Маскот природознавства'),
  logic: asset(`${BRAND_ROOT}/heroes/hero-logic.png`, [1024, 1024], true, 'Маскот логіки'),
};

export const AVATAR_ASSETS: Record<string, BrandAsset> = {
  bohdan: asset(`${BRAND_ROOT}/avatars/avatar-bohdan.png`, [512, 512], true, 'Аватар Богдана'),
  marko: asset(`${BRAND_ROOT}/avatars/avatar-marko.png`, [512, 512], true, 'Аватар Марка'),
  parent: asset(`${BRAND_ROOT}/avatars/avatar-parent.png`, [512, 512], true, 'Аватар батька'),
};

export const MODE_ASSETS = {
  training: asset(`${BRAND_ROOT}/modes/mode-training.png`, [768, 768], true, 'Режим «Тренування»'),
  showdown: asset(`${BRAND_ROOT}/modes/mode-showdown.png`, [768, 768], true, 'Режим «Сутичка»'),
  challenge: asset(`${BRAND_ROOT}/modes/mode-challenge.png`, [768, 768], true, 'Режим «Виклик»'),
} as const;

/** Ранги дороги трофеїв. Поріг — сумарні трофеї дитини. */
export const RANK_TIERS = [
  { key: 'rookie', title: 'Новачок', minTrophies: 0, asset: `${BRAND_ROOT}/ranks/rank-1-rookie.png` },
  { key: 'bronze', title: 'Бронза', minTrophies: 150, asset: `${BRAND_ROOT}/ranks/rank-2-bronze.png` },
  { key: 'silver', title: 'Срібло', minTrophies: 400, asset: `${BRAND_ROOT}/ranks/rank-3-silver.png` },
  { key: 'gold', title: 'Золото', minTrophies: 800, asset: `${BRAND_ROOT}/ranks/rank-4-gold.png` },
  { key: 'diamond', title: 'Діамант', minTrophies: 1400, asset: `${BRAND_ROOT}/ranks/rank-5-diamond.png` },
  { key: 'mythic', title: 'Міфік', minTrophies: 2200, asset: `${BRAND_ROOT}/ranks/rank-6-mythic.png` },
  { key: 'legend', title: 'Легенда', minTrophies: 3200, asset: `${BRAND_ROOT}/ranks/rank-7-legend.png` },
  { key: 'master', title: 'Майстер', minTrophies: 4500, asset: `${BRAND_ROOT}/ranks/rank-8-master.png` },
] as const;

export type RankTier = (typeof RANK_TIERS)[number];

export function rankForTrophies(trophies: number): RankTier {
  let current: RankTier = RANK_TIERS[0];
  for (const tier of RANK_TIERS) if (trophies >= tier.minTrophies) current = tier;
  return current;
}

export const UI_ASSETS = {
  logoFull: asset(`${BRAND_ROOT}/logo/logo-full.png`, [1536, 512], true, 'Повний логотип'),
  logoMark: asset(`${BRAND_ROOT}/logo/logo-mark.png`, [512, 512], true, 'Знак без тексту'),
  bgArena: asset(`${BRAND_ROOT}/ui/bg-arena.png`, [1536, 1024], false, 'Фон головного екрана'),
  bgLogin: asset(`${BRAND_ROOT}/ui/bg-login.png`, [1536, 1024], false, 'Фон екрана входу'),
  bgQuiz: asset(`${BRAND_ROOT}/ui/bg-quiz.png`, [1536, 1024], false, 'Фон екрана питання'),
  coin: asset(`${BRAND_ROOT}/ui/coin.png`, [512, 512], true, 'Монета — 1 бал = 1 грн'),
  coinStack: asset(`${BRAND_ROOT}/ui/coin-stack.png`, [512, 512], true, 'Стос монет для гаманця'),
  trophy: asset(`${BRAND_ROOT}/ui/trophy.png`, [512, 512], true, 'Трофей'),
  streakFlame: asset(`${BRAND_ROOT}/ui/streak-flame.png`, [512, 512], true, 'Полумʼя серії'),
  lock: asset(`${BRAND_ROOT}/ui/lock.png`, [512, 512], true, 'Замок заблокованого вмісту'),
  energy: asset(`${BRAND_ROOT}/ui/energy.png`, [512, 512], true, 'Енергія денного ліміту'),
} as const;

export const REWARD_BOX_ASSETS = {
  common: asset(`${BRAND_ROOT}/rewards/box-common.png`, [768, 768], true, 'Звичайна скринька'),
  rare: asset(`${BRAND_ROOT}/rewards/box-rare.png`, [768, 768], true, 'Рідкісна скринька'),
  epic: asset(`${BRAND_ROOT}/rewards/box-epic.png`, [768, 768], true, 'Епічна скринька'),
  legendary: asset(`${BRAND_ROOT}/rewards/box-legendary.png`, [768, 768], true, 'Легендарна скринька'),
} as const;

export const FX_ASSETS = {
  correctBurst: asset(`${BRAND_ROOT}/fx/correct-burst.png`, [768, 768], true, 'Спалах правильної відповіді'),
  wrongSlash: asset(`${BRAND_ROOT}/fx/wrong-slash.png`, [768, 768], true, 'Помилка'),
  levelUp: asset(`${BRAND_ROOT}/fx/level-up.png`, [1024, 1024], true, 'Підвищення рівня навички'),
  confetti: asset(`${BRAND_ROOT}/fx/confetti.png`, [1024, 1024], true, 'Конфеті завершення сесії'),
} as const;

/** Досягнення. `id` збігається з ключем у таблиці achievements. */
export const ACHIEVEMENTS = [
  { id: 'first_blood', title: 'Перша перемога', description: 'Правильна відповідь у першій сесії.', asset: `${BRAND_ROOT}/achievements/ach-first-blood.png` },
  { id: 'streak_10', title: 'Серія 10', description: 'Десять правильних поспіль.', asset: `${BRAND_ROOT}/achievements/ach-streak-10.png` },
  { id: 'streak_25', title: 'Незупинний', description: 'Двадцять пʼять правильних поспіль.', asset: `${BRAND_ROOT}/achievements/ach-streak-25.png` },
  { id: 'week_streak', title: 'Тиждень поспіль', description: 'Сім днів навчання без пропуску.', asset: `${BRAND_ROOT}/achievements/ach-week-streak.png` },
  { id: 'month_streak', title: 'Місяць поспіль', description: 'Тридцять днів без пропуску.', asset: `${BRAND_ROOT}/achievements/ach-month-streak.png` },
  { id: 'challenger', title: 'Сміливець', description: 'Пройшов сесію в режимі «Виклик».', asset: `${BRAND_ROOT}/achievements/ach-challenger.png` },
  { id: 'flawless', title: 'Бездоганно', description: 'Сесія без жодної помилки.', asset: `${BRAND_ROOT}/achievements/ach-flawless.png` },
  { id: 'redemption', title: 'Робота над помилками', description: 'Виправив десять колишніх помилок.', asset: `${BRAND_ROOT}/achievements/ach-redemption.png` },
  { id: 'math_master', title: 'Володар чисел', description: 'Опанував усі базові навички математики.', asset: `${BRAND_ROOT}/achievements/ach-math-master.png` },
  { id: 'word_master', title: 'Володар слова', description: 'Опанував базову орфографію.', asset: `${BRAND_ROOT}/achievements/ach-word-master.png` },
  { id: 'polyglot', title: 'Поліглот', description: 'Сто правильних відповідей з англійської.', asset: `${BRAND_ROOT}/achievements/ach-polyglot.png` },
  { id: 'explorer', title: 'Дослідник', description: 'Спробував усі пʼять предметів за один день.', asset: `${BRAND_ROOT}/achievements/ach-explorer.png` },
] as const;

export const PWA_ICONS = [
  asset('/icons/icon-192.png', [192, 192], false, 'Іконка PWA 192'),
  asset('/icons/icon-512.png', [512, 512], false, 'Іконка PWA 512'),
  asset('/icons/icon-maskable-512.png', [512, 512], false, 'Maskable-іконка PWA'),
  asset('/icons/apple-touch-icon.png', [180, 180], false, 'Іконка для iOS'),
];

/** Пласкій список усіх файлів — для CI-перевірки наявності. */
export const ALL_BRAND_ASSETS: BrandAsset[] = [
  ...Object.values(HERO_ASSETS),
  ...Object.values(AVATAR_ASSETS),
  ...Object.values(MODE_ASSETS),
  ...Object.values(UI_ASSETS),
  ...Object.values(REWARD_BOX_ASSETS),
  ...Object.values(FX_ASSETS),
  ...RANK_TIERS.map((r) => asset(r.asset, [512, 512], true, `Ранг: ${r.title}`)),
  ...ACHIEVEMENTS.map((a) => asset(a.asset, [512, 512], true, `Досягнення: ${a.title}`)),
  ...PWA_ICONS,
];
